import { LOCATION_CHANGE, push } from 'connected-react-router';

import { createMiddleware } from '../../../shared/redux';
import { ROUTES } from '../auth.constants';
import * as AuthService from '../auth-service';
import {
    acceptSignUp,
    acceptSignUpStepSubmit,
    getSignUpData,
    issueSignUpConfirmation,
    raiseSignUpUnknownException,
    rejectSignUp,
    rejectSignUpStepSubmit,
    resetSignUp,
    SIGN_UP_ACCEPTED,
    SIGN_UP_CONFIRMATION_ISSUED,
    SIGN_UP_REJECTED,
    SIGN_UP_STEP_SUBMIT_ACCEPTED,
    SIGN_UP_STEP_SUBMIT_REJECTED,
    SIGN_UP_STEP_SUBMIT_REQUESTED,
    SIGN_UP_GO_BACK_STEP,
    SIGN_UP_SUBMITTED,
    submitSignUp,
    getSignUpLastValidStep,
    setSignUpStep,
    SIGN_UP_VALIDATE_USERNAME,
    updateSignUpStepError,
} from './sign-up-state';
import { setSignInFlags, signIn } from '../sign-in/sign-in.state';
import { validateStep } from './sign-up.validation';
import { redirectToHome, redirectToSignIn } from '../auth-base-state';
import { ALL_STEPS, LAST_STEP } from './sign-up.constants';
import gaTrackerEvent from '../../../shared/utils/analyticsTracker';
import { validateUsername } from '../sign-up-single-page/sign-up-single-page.validation';

const getStepUrl = step => (step === 1 ? ROUTES.SIGN_UP : `${ROUTES.SIGN_UP}/${ALL_STEPS[step]}`);
const pushStep = step => push(getStepUrl(step), { step });
const isSkippablePathnameForLocalChangeAction = pathname => (
    !pathname.startsWith(ROUTES.SIGN_UP) || pathname.includes(ROUTES.SIGN_UP_LOADING)
);

export const signUpMiddleware = createMiddleware({
    [LOCATION_CHANGE]: async (state, action) => {
        const { pathname } = action.payload.location;
        const step = action.payload.location?.state?.step;

        if (isSkippablePathnameForLocalChangeAction(pathname)) return [];

        const lastValidStep = getSignUpLastValidStep(state);
        if (step) {
            if (lastValidStep === LAST_STEP) return push(ROUTES.SIGN_UP_CONFIRMATION);
            if (step > lastValidStep + 1) return pushStep(lastValidStep + 1);

            return setSignUpStep({ step });
        }

        return AuthService.currentAuthenticatedUser()
            .then(() => redirectToHome())
            .catch(() => [
                pushStep(1),
            ]);
    },

    [SIGN_UP_SUBMITTED]: (state) => {
        const data = getSignUpData(state);

        gaTrackerEvent(
            'AuthSignUp',
            'Submit',
            data.username,
        );

        return (
            AuthService.signUp({
                username: data.username,
                password: data.password,
                attributes: {
                    email: data.username,
                    name: data.name,
                    phone_number: data.phone,
                    'custom:company_name': data.companyName,
                    'custom:company_sector': data.companySector,
                    'custom:company_size': data.companySize,
                    'custom:employee_position': data.employeePosition,
                },
            })
                .then(user => (
                    user.userConfirmed
                        ? acceptSignUp({ user })
                        : issueSignUpConfirmation({ username: data.username })
                ))
                .catch(error => rejectSignUp({ error }))
        );
    },
    [SIGN_UP_ACCEPTED]: (state) => {
        const { username, password } = getSignUpData(state);

        gaTrackerEvent(
            'AuthSignUp',
            'SubmitAccepted',
        );

        return [
            resetSignUp(),
            setSignInFlags({ fromSignUp: true }),
            signIn({ username, password }),
        ];
    },
    [SIGN_UP_REJECTED]: (_, action) => {
        gaTrackerEvent(
            'AuthSignUp',
            'SubmitRejected',
        );

        return raiseSignUpUnknownException({
            message: action.payload.error.message,
        });
    },
    [SIGN_UP_CONFIRMATION_ISSUED]: (_, action) => push(ROUTES.SIGN_UP_CONFIRMATION, {
        username: action.payload.username,
    }),
    [SIGN_UP_STEP_SUBMIT_REQUESTED]: (_, action) => {
        gaTrackerEvent(
            'AuthSignUp',
            `StepSubmit - ${ALL_STEPS[action.payload.step] || 'user-email'}`,
        );

        return (
            validateStep({
                step: action.payload.step,
                data: action.payload.data,
            })
                .then(() => acceptSignUpStepSubmit({
                    step: action.payload.step,
                    data: action.payload.data,
                    hasNextStep: action.payload.hasNextStep,
                }))
                .catch(error => rejectSignUpStepSubmit({
                    step: action.payload.step,
                    error,
                }))
        );
    },
    [SIGN_UP_STEP_SUBMIT_ACCEPTED]: (_, action) => {
        gaTrackerEvent(
            'AuthSignUp',
            `StepSubmitAccepted - ${ALL_STEPS[action.payload.step] || 'user-email'}`,
        );

        return (
            action.payload.hasNextStep
                ? pushStep(action.payload.step + 1)
                : submitSignUp()
        );
    },
    [SIGN_UP_STEP_SUBMIT_REJECTED]: (_, action) => {
        gaTrackerEvent(
            'AuthSignUp',
            `StepSubmitRejected - ${ALL_STEPS[action.payload.step] || 'user-email'}`,
        );
    },
    [SIGN_UP_GO_BACK_STEP]: (_, action) => (
        action.payload.hasPreviousStep
            ? pushStep(action.payload.step - 1)
            : [resetSignUp(), redirectToSignIn()]
    ),
    [SIGN_UP_VALIDATE_USERNAME]: (_, action) => (
        validateUsername({ data: action.payload.username })
            .then(() => updateSignUpStepError({
                error: { ...action.payload.error, username: null },
            }))
            .catch(e => rejectSignUpStepSubmit({
                error: { ...action.payload.error, username: Object.values(e)[0] },
            }))
    ),
});
